import { useMemo } from 'react';
import dayjs from 'dayjs';
import { styled } from '@mui/material';
import { Box, Typography } from '@mui/material';

import { calculateSecondsToHoursAndMinutes } from '@libs/dateTime';
import {
    TimesheetsNoDataFound,
    MonthCalendar,
    AttendanceSummaryChips,
    MonthlySummaryTable
} from '../../../components';
import {
    buildMonthDaySheet,
    summarizeDays
} from 'timeAndAttendance/statusTokens';
import { useYearlyTimesheetsState } from '../useYearlyTimesheets';

const UsersInfoBlock = styled('div')(({ theme: { spacing } }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    maxWidth: '100%',
    marginBottom: '1em',
    gap: '2em 5em'
}));

const EmptyPrompt = styled('div')(({ theme: { spacing, palette } }) => ({
    width: '100%',
    padding: spacing(6, 2),
    textAlign: 'center',
    color: palette.text.secondary
}));

/**
 * Admin yearly timesheet — one employee's whole year, same design as the
 * "My Attendance" yearly view: employee info + year totals, per-status
 * chips, a wall of 12 mini month calendars, then the month-by-month
 * summary table (counts + hours).
 */
const UsersTimesheet = () => {
    const { data: users, email, year } = useYearlyTimesheetsState();
    const employee = users[0];

    const monthSheets = useMemo(() => {
        const entries = employee?.entries || [];
        const bounds = {
            joiningDate: employee?.joiningDate,
            resignationDate: employee?.resignationDate
        };
        return Array.from({ length: 12 }, (_, index) => ({
            month: index + 1,
            title: dayjs(`${year}-${index + 1}-01`).format('MMMM'),
            daySheet: buildMonthDaySheet(
                entries,
                `${year}-${index + 1}`,
                bounds
            )
        }));
    }, [employee, year]);

    const yearSummary = useMemo(
        () => summarizeDays(monthSheets.flatMap((m) => m.daySheet)),
        [monthSheets]
    );

    if (users.length === 0 && !email) {
        return (
            <EmptyPrompt>
                Search an employee by email to load their yearly timesheet.
            </EmptyPrompt>
        );
    }

    if (users.length === 0) return <TimesheetsNoDataFound />;

    return (
        <Box sx={{ width: '100%', px: { xs: 0.5, sm: 2 }, pb: 2 }}>
            <UsersInfoBlock>
                <div>
                    <b>Employee : </b>
                    <span>{employee?.name}</span>
                </div>
                <div>
                    <b>Email : </b>
                    <span>{employee?.email}</span>
                </div>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ marginLeft: 'auto' }}
                >
                    Worked{' '}
                    {calculateSecondsToHoursAndMinutes(
                        employee?.totalDuration || 0
                    )}
                    h of{' '}
                    {calculateSecondsToHoursAndMinutes(
                        employee?.totalShiftDuration || 0
                    )}
                    h in {year}
                </Typography>
            </UsersInfoBlock>

            <AttendanceSummaryChips summary={yearSummary} sx={{ mb: 2 }} />

            <Box
                sx={{
                    display: 'grid',
                    gap: { xs: 2.5, sm: 3 },
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    }
                }}
            >
                {monthSheets.map(({ month, title, daySheet }) => (
                    <MonthCalendar
                        key={month}
                        compact
                        title={title}
                        daySheet={daySheet}
                        yearMonth={`${year}-${month}`}
                    />
                ))}
            </Box>

            <MonthlySummaryTable
                monthSheets={monthSheets}
                monthlyTotals={employee?.monthlyTotals}
                sx={{ mt: 4 }}
            />
        </Box>
    );
};

export default UsersTimesheet;
